import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  Check, 
  Handshake, 
  MapPin, 
  Calculator, 
  Layers, 
  CheckCircle2, 
  Ruler, 
  FileText, 
  Home as HomeIcon, 
  Building, 
  Warehouse, 
  TreePine, 
  ShieldCheck, 
  Cpu, 
  FileSpreadsheet,
  Zap,
  Shield,
  Building2,
  Phone,
  Mail
} from 'lucide-react';
import Layout from '../components/Layout';
import IconBox from '../components/IconBox';
import ProjectCard from '../components/ProjectCard';
import ContactForm from '../components/ContactForm';
import { fetchProjects } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/utils';

const partners = [
  {
    name: 'WSF Schroeffundering i Holland',
    role: 'Leverandør',
    logo: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/420efba5-d257-4879-9811-77fbed433154.webp',
    alt: 'WSF Schroeffundering Holland – Leverandør af varmgalvaniserede skruefundamenter',
    caption: 'Kvalitetsleverandør af skruefundamenter i Europa',
    description: 'Førende leverandør af skruefundamenter - kort leveringstid.',
    website: 'https://schroeffundering.nl/'
  },
  {
    name: 'Pilar ApS',
    role: 'Test & Dokumentation',
    logo: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/19de61d7-e8e8-456b-b0f6-0c1d95aad745.png',
    alt: 'Pilar ApS – Test, dokumentation og eftervisning af bæreevne for skruefundamenter',
    caption: 'Uafhængig test & dokumentation af bæreevne',
    description: 'Det nyeste indenfor test, dokumentation og eftervisning af bæreevne. Nødvendig dokumentation uanset konstruktionsklasse.',
    website: 'https://pilar.dk/'
  },
  {
    name: 'Kjellerup En3prenøren 2025 ApS',
    role: 'Montering',
    logo: 'https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/a1053cd9-3f13-4fae-bf0a-938a8cc2d0d9.svg',
    alt: 'Kjellerup En3prenøren 2025 ApS – Professionel montering af skruefundamenter',
    caption: 'Erfaren montør af skruefundamenter i Midtjylland',
    description: 'Montering af skruefundamenter. Fleksibel og mange års erfaring.',
    website: 'https://kjellerup-en3prenøren.dk/'
  }
];

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = [
    {
      url: "https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg",
      alt: "Installation af jordskruer med præcisionsmaskine til byggefundament i hele Danmark",
      caption: "Præcis skruefundering uden opgravning"
    },
    {
      url: "https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/8d2c9fa9-ae95-4a83-baf6-553f65a797bd.jpg",
      alt: "Montering af skruefundament og jordskruer til sommerhus og træterrasse",
      caption: "Fundament til sommerhuse og terrasser"
    },
    {
      url: "https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/61b8ba39-d386-4b7a-8305-9a3fd7d7d95b.jpg",
      alt: "Pæleforankring og skruefundering uden beton på byggeplads",
      caption: "Bæredygtig fundering uden beton og tørretid"
    },
    {
      url: "https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/18c75725-37a6-4c9c-90c1-34d1b169fb97.jpg",
      alt: "Jordskruer i varmgalvaniseret stål monteret sikkert i bærende jordlag",
      caption: "Varmgalvaniseret stål med 100+ års levetid"
    },
    {
      url: "https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/0522f5f5-4ecc-4813-826c-5ce70aebf354.jpg",
      alt: "Færdigmonteret skruefundament med bærebeslag klar til trækonstruktion",
      caption: "Klar til opbygning straks efter montage"
    }
  ];

  useEffect(() => {
    fetchProjects().then(setProjects);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  // Optimerede billeder
  const optimizedHeroImages = heroImages.map(item => ({
    ...item,
    optimizedUrl: getOptimizedImageUrl(item.url, { width: 2000, quality: 95 })
  }));
  const aboutImageUrl = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/aarhus-street-food-skruefundament-1.jpg", { width: 1200, quality: 90 });
  const technicalScrewUrl = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/jordskruer-teknisk-detalje.jpg", { width: 900, quality: 90 });
  const summerhouseImgUrl = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/traeterrasse-skruefundament.jpg", { width: 800, quality: 90 });
  const carportImgUrl = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/carport-og-skur-skruefundament.jpg", { width: 800, quality: 90 });
  const modularImgUrl = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/modulbyggeri-skruefundament.jpg", { width: 800, quality: 90 });

  // Struktureret data (JSON-LD) for LocalBusiness
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "CM Skruefundament ApS",
    "image": "https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/34a78654-9cb6-4b5b-93fb-23a3c5578977.png",
    "url": "https://cmskruefundament.dk",
    "telephone": "+4526796139",
    "email": "cs@cmskruefundament.dk",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Skolevænget 14",
      "addressLocality": "Horsens",
      "postalCode": "8700",
      "addressCountry": "DK"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "55.8606",
      "longitude": "9.8504"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Danmark"
    },
    "priceRange": "$$"
  };

  return (
    <Layout>
      <Helmet>
        <title>Stærke skruefundamenter til byggeri i hele Danmark | Ring nu</title>
        <meta name="description" content="Vi leverer skruefundamenter til private og erhverv i hele Danmark. Effektiv, præcis og bæredygtig fundering uden omfattende gravearbejde her." />
        <link rel="canonical" href="https://cmskruefundament.dk/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cmskruefundament.dk/" />
        <meta property="og:title" content="Stærke skruefundamenter til byggeri i hele Danmark | Ring nu" />
        <meta property="og:description" content="Vi leverer skruefundamenter til private og erhverv i hele Danmark. Effektiv, præcis og bæredygtig fundering uden omfattende gravearbejde her." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="CM Skruefundament ApS" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cmskruefundament.dk/" />
        <meta name="twitter:title" content="Stærke skruefundamenter til byggeri i hele Danmark | Ring nu" />
        <meta name="twitter:description" content="Vi leverer skruefundamenter til private og erhverv i hele Danmark. Effektiv, præcis og bæredygtig fundering uden omfattende gravearbejde her." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/1579e6f7-9ff1-4e95-aba1-981ac1255ac4.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 lg:py-28 overflow-hidden min-h-[700px] flex items-center">
        <div className="absolute inset-0 z-0">
          {optimizedHeroImages.map((imageObj, index) => (
            <img
              key={imageObj.url}
              src={imageObj.optimizedUrl}
              alt={imageObj.alt}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "auto"}
            />
          ))}
          <div className="absolute inset-0 bg-slate-950/75 backdrop-blur-[1px]"></div>
          {/* Subtle Hero Image Caption */}
          <div className="absolute bottom-4 left-6 z-10 hidden md:block">
            <span className="text-white/70 text-xs tracking-wider bg-slate-950/70 px-3 py-1.5 rounded-lg backdrop-blur-xs border border-white/10">
              {optimizedHeroImages[currentImageIndex]?.caption}
            </span>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white w-full">
          <div className="max-w-4xl">
            
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-slate-200 text-xs font-bold uppercase tracking-widest mb-6">
              <ShieldCheck className="w-4 h-4 text-amber-400" />
              <span>Professionel Fundering i Hele Danmark</span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-6xl font-black uppercase leading-[1.08] tracking-tight mb-6">
              Stærke skruefundamenter til byggeri i hele Danmark
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-200 mb-8 leading-relaxed max-w-3xl font-light">
              Effektiv, præcis og bæredygtig fundering uden omfattende gravearbejde. 
              Vi skaber det perfekte grundlag for dit næste projekt – fra terrasser til modulbyggeri og store erhvervshaller.
            </p>

            {/* Key Conversion Bullets */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-10 max-w-3xl">
              <div className="flex items-center gap-2.5 bg-slate-900/60 backdrop-blur-xs px-3.5 py-2.5 rounded-xl border border-white/10">
                <div className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs sm:text-sm text-slate-200 font-medium">
                  <strong className="text-white">0 timers hærdetid</strong> – byg straks
                </span>
              </div>
              <div className="flex items-center gap-2.5 bg-slate-900/60 backdrop-blur-xs px-3.5 py-2.5 rounded-xl border border-white/10">
                <div className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs sm:text-sm text-slate-200 font-medium">
                  <strong className="text-white">Ingen opgravning</strong> – skåner haven
                </span>
              </div>
              <div className="flex items-center gap-2.5 bg-slate-900/60 backdrop-blur-xs px-3.5 py-2.5 rounded-xl border border-white/10">
                <div className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs sm:text-sm text-slate-200 font-medium">
                  <strong className="text-white">Dokumenteret bæreevne</strong>
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 items-center">
              <a
                href="#tilbud"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 px-8 py-4 rounded-xl text-sm font-bold uppercase tracking-wider transition-all duration-200 shadow-xl shadow-amber-500/20 inline-flex items-center gap-2"
              >
                <span>Få et uforpligtende tilbud</span>
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/projekter"
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-7 py-4 rounded-xl text-sm font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-2 backdrop-blur-xs"
              >
                <span>Se vores projekter</span>
              </Link>
              <a
                href="tel:+4526796139"
                className="text-white/90 hover:text-white px-4 py-4 text-sm font-semibold inline-flex items-center gap-2 transition-colors ml-auto sm:ml-0"
              >
                <Phone className="w-4 h-4 text-amber-400" />
                <span>+45 26 79 61 39</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="bg-slate-900 py-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 text-center">
            <IconBox
              icon="Zap"
              title="Hurtig montering"
              description="Vi monterer fundamentet på få timer, og du kan bygge videre med det samme. Ingen ventetid på tørrende beton."
            />
            <IconBox
              icon="Shield"
              title="Minimal jordpåvirkning"
              description="Ingen tung gravemaskine eller bortkørsel af jord. Vi skåner haven og miljøet omkring byggepladsen."
            />
            <IconBox
              icon="CheckCircle2"
              title="Stærkt og holdbart"
              description="Vores skruefundamenter er testet til ekstreme belastninger og har en lang levetid i det danske klima."
            />
            <IconBox
              icon="Building2"
              title="Private og erhverv"
              description="Fra terrasser og carporte til store modulbyggerier og erhvervshaller. Vi har løsningen til det hele."
            />
          </div>
        </div>
      </section>

      {/* Early Quick-Quote Conversion Section */}
      <section id="tilbud" className="py-16 md:py-20 bg-linear-to-b from-slate-900 to-slate-950 text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            
            {/* Left Value & Contact Intro */}
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs font-bold uppercase tracking-widest mb-4">
                <span>Hurtig prisberegning</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold uppercase tracking-tight text-white mb-5 leading-tight">
                Få et uforpligtende tilbud på dit projekt
              </h2>
              <p className="text-slate-300 text-base sm:text-lg mb-6 leading-relaxed">
                Udfyld formularen med dine projektønsker eller vedhæft tegninger. Vi vender hurtigt tilbage med en skarp pris og professionel rådgivning.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="text-white block text-sm">Gratis og uforpligtende</strong>
                    <span className="text-slate-400 text-xs sm:text-sm">Ingen binding – du får et gennemskueligt tilbud.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="text-white block text-sm">Hurtig respons</strong>
                    <span className="text-slate-400 text-xs sm:text-sm">Vi besvarer typisk henvendelser inden for få timer på hverdage.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="text-white block text-sm">Rådgivning og beregning</strong>
                    <span className="text-slate-400 text-xs sm:text-sm">Vi hjælper med valg af skruetype og bæreevne til din jordbund.</span>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-800 flex flex-col sm:flex-row gap-4 text-sm">
                <a
                  href="tel:+4526796139"
                  className="inline-flex items-center gap-2.5 text-slate-200 hover:text-white font-semibold transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-amber-400">
                    <Phone className="w-4 h-4" />
                  </div>
                  <span>+45 26 79 61 39</span>
                </a>
                <a
                  href="mailto:cs@cmskruefundament.dk"
                  className="inline-flex items-center gap-2.5 text-slate-200 hover:text-white font-semibold transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-amber-400">
                    <Mail className="w-4 h-4" />
                  </div>
                  <span>cs@cmskruefundament.dk</span>
                </a>
              </div>
            </div>

            {/* Right: Embedded Form */}
            <div className="lg:col-span-7">
              <ContactForm
                variant="hero"
                defaultService="Skruefundering generelt"
                title="Send din forespørgsel"
                subtitle="Udfyld formularen herunder, så kontakter vi dig hurtigst muligt."
              />
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 1: Hvad er et skruefundament, og hvorfor er det værd at vælge? */}
      <section className="py-20 lg:py-28 bg-white border-b border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 text-slate-800 text-xs font-bold uppercase tracking-widest mb-6">
                <Layers className="w-3.5 h-3.5 text-slate-700" />
                <span>Skruefundament</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 uppercase tracking-tight leading-tight mb-8">
                Hvad er et skruefundament, og hvorfor er det værd at vælge?
              </h2>

              <div className="space-y-6 text-slate-600 text-base sm:text-lg leading-relaxed mb-8">
                <p>
                  Et skruefundament består af kraftige jordskruer i stål, som overfører konstruktionens belastning til de bærende jordlag. Hver løsning tilpasses jordbunden og det byggeri, der skal stå ovenpå. Det giver et præcist fundament med mindre indgreb på grunden end ved traditionel udgravning og støbning.
                </p>
                <p>
                  Ved <Link to="/skruefundering" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">skruefundering</Link> er der ingen ventetid på hærdning. Når fundamentet er monteret og kontrolleret, kan arbejdet på konstruktionen fortsætte. Det kan forkorte byggeprocessen og begrænse behovet for jordhåndtering, samtidig med at stålskruerne kan demonteres og i mange tilfælde genanvendes.
                </p>
              </div>

              {/* Visual highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-150">
                <div className="p-4 bg-slate-50 border border-slate-100 rounded-lg">
                  <div className="font-black text-2xl text-slate-900 mb-1">0 timer</div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Ventetid på hærdning</p>
                </div>
                <div className="p-4 bg-slate-50 border border-slate-100 rounded-lg">
                  <div className="font-black text-2xl text-slate-900 mb-1">100%</div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Genanvendeligt stål</p>
                </div>
                <div className="p-4 bg-slate-50 border border-slate-100 rounded-lg">
                  <div className="font-black text-2xl text-slate-900 mb-1">Minimalt</div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Indgreb i haven</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="bg-slate-900 text-white rounded-2xl p-8 lg:p-10 shadow-2xl relative z-10 overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-slate-800 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none opacity-60"></div>
                  
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-white/10 rounded-xl">
                      <ShieldCheck className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-widest text-slate-400 font-bold block">Dokumenteret kvalitet</span>
                      <h4 className="text-lg font-bold uppercase text-white">Præcis overførsel til bærelag</h4>
                    </div>
                  </div>

                  <div className="space-y-4 text-sm text-slate-300 leading-relaxed mb-8">
                    <div className="flex items-start gap-3 bg-white/5 p-3.5 rounded-lg border border-white/10">
                      <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <span>Kraftige jordskruer i galvaniseret stål dimensioneret efter jordbund og last.</span>
                    </div>
                    <div className="flex items-start gap-3 bg-white/5 p-3.5 rounded-lg border border-white/10">
                      <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <span>Arbejdet fortsætter med det samme efter montering og kontrol.</span>
                    </div>
                    <div className="flex items-start gap-3 bg-white/5 p-3.5 rounded-lg border border-white/10">
                      <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <span>Bæredygtig demontering og genanvendelse efter endt brug.</span>
                    </div>
                  </div>

                  <Link
                    to="/skruer"
                    className="flex items-center justify-between w-full bg-white text-slate-950 font-bold uppercase tracking-widest text-xs px-6 py-4 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <span>Udforsk skruetyper & tekniske data</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="hidden sm:block absolute -bottom-6 -right-6 w-full h-full border-2 border-slate-200 rounded-2xl -z-0"></div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 2: Fra terrasse og skur til et komplet sommerhus */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3 block">
              Fleksible anvendelsesmuligheder
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 uppercase tracking-tight leading-tight mb-6">
              Fra terrasse og skur til et komplet sommerhus
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Skruefundamenter kan bruges under terrasser, skure, carporte, udestuer, modulbyggeri og flere typer boliger. Et skruefundament til et skur giver en hurtig vej til et stabilt underlag uden et støbt punktfundament. Til større konstruktioner vurderer vi både belastning, jordbund og krav til dokumentation.
            </p>
          </div>

          {/* Visual Application Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            
            {/* Card 1: Terrasse & Skur */}
            <div className="bg-white rounded-xl overflow-hidden border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
              <figure className="relative h-48 overflow-hidden m-0">
                <img 
                  src={summerhouseImgUrl} 
                  alt="Skruefundament og jordskruer til træterrasse og haveskur uden betonstøbning" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-transparent to-transparent"></div>
                <figcaption className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
                  <div className="flex items-center gap-2">
                    <TreePine className="w-5 h-5 text-amber-400" />
                    <span className="font-bold uppercase tracking-wider text-sm">Terrasser & Skure</span>
                  </div>
                  <span className="text-[11px] text-slate-300 font-medium hidden sm:inline">Uden støbning</span>
                </figcaption>
              </figure>
              <div className="p-6 flex-1 flex flex-col justify-between">
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  Hurtig og stabil vej til et solidt underlag uden besværlig betonstøbning eller punktfundamenter.
                </p>
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-900">
                  <span>Mindre indgreb</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </div>

            {/* Card 2: Carport & Udestue */}
            <div className="bg-white rounded-xl overflow-hidden border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
              <figure className="relative h-48 overflow-hidden m-0">
                <img 
                  src={carportImgUrl} 
                  alt="Skruefundament til carport og udestue med høj trækstyrke og stabilitet" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-transparent to-transparent"></div>
                <figcaption className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
                  <div className="flex items-center gap-2">
                    <Warehouse className="w-5 h-5 text-amber-400" />
                    <span className="font-bold uppercase tracking-wider text-sm">Carporte & Udestuer</span>
                  </div>
                  <span className="text-[11px] text-slate-300 font-medium hidden sm:inline">Høj vindlast</span>
                </figcaption>
              </figure>
              <div className="p-6 flex-1 flex flex-col justify-between">
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  Præcis forankring mod både tryk og træk, så din tilbygning står sikkert i storm og blæst.
                </p>
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-900">
                  <span>Høj trækstyrke</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </div>

            {/* Card 3: Modulbyggeri & Boliger */}
            <div className="bg-white rounded-xl overflow-hidden border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
              <figure className="relative h-48 overflow-hidden m-0">
                <img 
                  src={modularImgUrl} 
                  alt="Ingeniørberegnede skruefundamenter til modulbyggeri og permanente boliger" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-transparent to-transparent"></div>
                <figcaption className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
                  <div className="flex items-center gap-2">
                    <Building className="w-5 h-5 text-amber-400" />
                    <span className="font-bold uppercase tracking-wider text-sm">Modulbyggeri & Bolig</span>
                  </div>
                  <span className="text-[11px] text-slate-300 font-medium hidden sm:inline">Fuld godkendelse</span>
                </figcaption>
              </figure>
              <div className="p-6 flex-1 flex flex-col justify-between">
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  Ingeniørberegnede løsninger med fuld dokumentation og eftervisning af bæreevne til større byggerier.
                </p>
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-900">
                  <span>Fuld dokumentation</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </div>

          </div>

          {/* Sub-block: Skruefundament til sommerhus */}
          <div className="bg-white rounded-2xl p-8 lg:p-12 border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-slate-900 text-white text-xs font-bold uppercase tracking-wider mb-4">
                  <HomeIcon className="w-3.5 h-3.5" />
                  <span>Dimensionering & Rådgivning</span>
                </div>
                
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold uppercase text-slate-900 tracking-tight mb-4">
                  Skruefundament til sommerhus – få løsningen dimensioneret rigtigt
                </h3>
                
                <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                  Skal et sommerhus stå på skruefundament, skal løsningen passe til bygningens vægt, opbygning og jordbundsforholdene på grunden. Vi vurderer derfor opgaven, før skruernes diameter og længde vælges. Samme faglige tilgang gælder ved helårshuse og tilbygninger, hvor beregning og dokumentation er afgørende for resultatet.
                </p>
              </div>

              <div className="lg:col-span-4 bg-slate-50 p-6 rounded-xl border border-slate-100 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-900"></div>
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-800">Sommerhuse & Fritidshuse</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-900"></div>
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-800">Helårshuse & Pavilloner</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-900"></div>
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-800">Tilbygninger & Udestuer</span>
                </div>
                <div className="pt-3 border-t border-slate-200/80">
                  <Link 
                    to="/kontakt"
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-900 hover:text-slate-600 transition-colors"
                  >
                    <span>Få rådgivning til dit sommerhus</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* SECTION 3: Skruefundament i Jylland, på Fyn og Sjælland – vi dækker hele Danmark */}
      <section className="py-20 lg:py-28 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 text-slate-200 text-xs font-bold uppercase tracking-widest mb-4">
              <MapPin className="w-3.5 h-3.5 text-slate-300" />
              <span>Landsdækkende Service</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight mb-6">
              Skruefundament i Jylland, på Fyn og Sjælland – vi dækker hele Danmark
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              Vi hjælper med skruefundamenter til projekter i hele Danmark – fra mindre konstruktioner til større byggerier. Fælles for opgaverne er, at fundamentet skal passe præcist til både konstruktionen og forholdene på grunden.
            </p>
          </div>

          {/* 3-Step Process Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            
            <div className="bg-white/5 border border-white/10 rounded-xl p-8 relative">
              <div className="text-4xl font-black text-slate-600 mb-4">01</div>
              <h3 className="text-xl font-bold uppercase tracking-tight mb-3 text-white">Gennemgang & Valg</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi starter med at gennemgå projektet og vælger derefter skruer ud fra konstruktionens belastning og jordens egenskaber.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-8 relative">
              <div className="text-4xl font-black text-slate-600 mb-4">02</div>
              <h3 className="text-xl font-bold uppercase tracking-tight mb-3 text-white">Laser-afsætning</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Fundamentpunkterne afsættes med laser, hvorefter skruerne føres ned til de bærende lag med specialudstyr.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-8 relative">
              <div className="text-4xl font-black text-slate-600 mb-4">03</div>
              <h3 className="text-xl font-bold uppercase tracking-tight mb-3 text-white">Bæreevnekontrol</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Bæreevnen kontrolleres under monteringen, så fundamentet passer til opgaven.
              </p>
            </div>

          </div>

          {/* Products & Self-build highlight card */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-850 border border-white/15 rounded-2xl p-8 lg:p-10">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div className="max-w-3xl">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-400 block mb-2">
                  Professionel montering &amp; selvbyg
                </span>
                <p className="text-slate-200 text-base sm:text-lg leading-relaxed mb-4">
                  <strong>CM Skruefundament</strong> hjælper både kunder, der ønsker professionel montering, og kunder, der søger produkter til eget projekt. Du kan også få viden om <Link to="/jordskruer" className="font-bold text-amber-300 underline decoration-amber-400 hover:text-amber-200 transition-colors">jordskruer</Link>, <Link to="/jordanker" className="font-bold text-amber-300 underline decoration-amber-400 hover:text-amber-200 transition-colors">jordanker</Link>, <Link to="/terrasseskruer" className="font-bold text-amber-300 underline decoration-amber-400 hover:text-amber-200 transition-colors">terrasseskruer</Link> til fundamentet under terrassen og <Link to="/groundplug" className="font-bold text-amber-300 underline decoration-amber-400 hover:text-amber-200 transition-colors">GroundPlug</Link>.
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Link to="/jordskruer" className="bg-white/10 hover:bg-amber-400 hover:text-slate-950 text-white text-xs px-3.5 py-1.5 rounded-md font-bold transition-all flex items-center gap-1.5">
                    <span>Jordskruer</span>
                    <ArrowRight className="w-3 h-3 text-amber-300 group-hover:text-slate-950" />
                  </Link>
                  <Link to="/jordanker" className="bg-white/10 hover:bg-amber-400 hover:text-slate-950 text-white text-xs px-3.5 py-1.5 rounded-md font-bold transition-all flex items-center gap-1.5">
                    <span>Jordanker</span>
                    <ArrowRight className="w-3 h-3 text-amber-300 group-hover:text-slate-950" />
                  </Link>
                  <Link to="/terrasseskruer" className="bg-white/10 hover:bg-amber-400 hover:text-slate-950 text-white text-xs px-3.5 py-1.5 rounded-md font-bold transition-all flex items-center gap-1.5">
                    <span>Terrasseskruer</span>
                    <ArrowRight className="w-3 h-3 text-amber-300 group-hover:text-slate-950" />
                  </Link>
                  <Link to="/groundplug" className="bg-white/10 hover:bg-amber-400 hover:text-slate-950 text-white text-xs px-3.5 py-1.5 rounded-md font-bold transition-all flex items-center gap-1.5">
                    <span>GroundPlug</span>
                    <ArrowRight className="w-3 h-3 text-amber-300 group-hover:text-slate-950" />
                  </Link>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full lg:w-auto shrink-0">
                <Link
                  to="/kontakt"
                  className="bg-white text-slate-950 px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-slate-100 transition-colors text-center rounded"
                >
                  Kontakt os
                </Link>
                <Link
                  to="/skruer"
                  className="border border-white/30 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-white/10 transition-colors text-center rounded"
                >
                  Se produkter
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 4: Hvad koster et skruefundament? */}
      <section className="py-20 lg:py-28 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 text-slate-800 text-xs font-bold uppercase tracking-widest mb-4">
                <Calculator className="w-3.5 h-3.5 text-slate-700" />
                <span>Prisoverslag &amp; Tilbud</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 uppercase tracking-tight leading-tight mb-6">
                Hvad koster et skruefundament?
              </h2>

              <div className="space-y-6 text-slate-600 text-base sm:text-lg leading-relaxed mb-8">
                <p>
                  Prisen på et skruefundament afhænger især af antallet af fundamentpunkter, skruernes dimensioner, jordbundsforholdene, adgangsforholdene og behovet for beregning eller dokumentation. Arealet fortæller derfor ikke hele historien, og vi vurderer altid den konkrete konstruktion, før vi kan give en præcis pris.
                </p>
                <p>
                  Send os gerne tegninger, mål og oplysninger om den planlagte konstruktion. Jo bedre vi kender projektet, desto mere præcist kan vi rådgive om produktvalg, dimensionering og montering. Du får et uforpligtende tilbud og et klart grundlag for at vælge den rette fundering til dit byggeri.
                </p>
              </div>

              <Link
                to="/kontakt"
                className="inline-flex items-center gap-3 bg-slate-900 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors shadow-md rounded"
              >
                <span>Indhent uforpligtende tilbud</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 lg:p-10">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-6">
                  Faktorer der afgør prisen
                </h3>

                <div className="space-y-4">
                  {[
                    { label: 'Antal fundamentpunkter', desc: 'Hvor mange skruer bærefladen forudsætter for stabil lastfordeling.' },
                    { label: 'Skruernes dimensioner', desc: 'Diameter, godstykkelse og længde tilpasset jordens bærelag.' },
                    { label: 'Jordbundsforhold', desc: 'Jordtypen (sand, ler, muld) og dybden til det faste, bærende lag.' },
                    { label: 'Adgangsforhold på grunden', desc: 'Tilgængelighed for specialmaskiner eller brug af håndholdt monteringsudstyr.' },
                    { label: 'Beregning & dokumentation', desc: 'Eventuelle krav til geoteknisk rapport, konstruktionsklasse og test.' }
                  ].map((factor, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-xl border border-slate-200/80 flex items-start gap-4">
                      <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                        {idx + 1}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wide">{factor.label}</h4>
                        <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">{factor.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* About / Why Choose Us Section (Existing) */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 block">Hvorfor vælge os?</span>
              <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-8">
                Fremtidens fundament er skruet i jorden
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Hos <Link to="/om-os" className="font-bold text-slate-900 underline decoration-amber-500 hover:text-amber-600 transition-colors">CM Skruefundament</Link> er skruefundamenter den moderne afløser for traditionel betonfundering. 
                Det er hurtigere, billigere og langt mere miljøvenligt. Vi anvender specialudviklede 
                stålskruer, der bores direkte ned i de bærende jordlag.
              </p>
              
              <div className="space-y-4 mb-10">
                {[
                  'Ingen ventetid på hærdning',
                  'Kan monteres i alt slags vejr',
                  'Præcis placering med millimeter-nøjagtighed',
                  'Nem at fjerne og genanvende (bæredygtigt)',
                  'Ingen skader på eksisterende belægning eller have'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="bg-slate-900 rounded-full p-1">
                      <Check className="text-white w-4 h-4" />
                    </div>
                    <span className="font-bold uppercase text-sm tracking-wide">{item}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/skruer"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors"
              >
                Læs mere om teknikken <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <figure className="m-0">
                <div className="aspect-square bg-white shadow-2xl overflow-hidden relative">
                  <img
                    src={aboutImageUrl}
                    alt="Teknisk montage af jordskruer og stålfundament med 100% præcision og lasermåling"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <figcaption className="mt-3 text-xs text-slate-500 font-medium text-right">
                  Præcisionsmontage af skruefundament med dokumenteret bæreevne
                </figcaption>
              </figure>
              <div className="absolute bottom-2 -left-8 bg-slate-900 text-white p-4 min-[992px]:p-[30px] hidden md:block shadow-2xl border border-slate-800 z-10">
                <p className="text-4xl font-black mb-1 text-amber-400">100%</p>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-200">Præcision & Styrke</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Preview (Existing) */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 block">Udvalgte Cases</span>
              <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight">
                Se vores arbejde i virkeligheden
              </h2>
            </div>
            <Link
              to="/projekter"
              className="text-sm font-bold uppercase tracking-widest border-b-2 border-slate-900 pb-1 hover:text-slate-500 hover:border-slate-500 transition-colors"
            >
              Se alle projekter
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...projects]
              .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
              .slice(0, 3)
              .map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
          </div>
        </div>
      </section>

      {/* Contact Section with Form */}
      <section id="kontakt" className="py-20 lg:py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-amber-500 mb-4 block">Uforpligtende tilbud</span>
              <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-6 text-white leading-tight">
                Skal vi hjælpe med dit næste fundament?
              </h2>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8">
                Udfyld formularen eller kontakt os direkte for en uforpligtende snak og et skarpt tilbud på dit projekt. Vi rådgiver, dimensionerer og monterer i hele Danmark.
              </p>

              <div className="space-y-4 mb-8">
                <a
                  href="tel:26796139"
                  className="flex items-center gap-4 p-4 bg-slate-800/90 rounded-lg border border-slate-700 hover:border-amber-500 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Ring direkte</p>
                    <p className="text-lg font-bold text-white">26 79 61 39</p>
                  </div>
                </a>

                <a
                  href="mailto:cs@cmskruefundament.dk"
                  className="flex items-center gap-4 p-4 bg-slate-800/90 rounded-lg border border-slate-700 hover:border-amber-500 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Send en mail</p>
                    <p className="text-base font-bold text-white">cs@cmskruefundament.dk</p>
                  </div>
                </a>
              </div>

              <div className="border-t border-slate-800 pt-6">
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Hvad kan du forvente?</h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Hurtig tilbagemelding og faglig sparring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Mulighed for at vedhæfte tegninger og beregninger</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Altid gratis og 100% uforpligtende tilbud</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section (Existing) */}
      <section className="py-24 bg-slate-50 border-t border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="text-xs font-bold uppercase tracking-[0.3em] mb-4 block text-slate-500">Stærke relationer</span>
            <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-6">Vores Samarbejdspartnere</h2>
            <p className="text-slate-600 text-lg">
              Vi samarbejder med branchens bedste aktører for at sikre dig den absolut højeste kvalitet, hurtig levering og fuld dokumentation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partners.map((partner, i) => {
              const optimizedLogo = getOptimizedImageUrl(partner.logo, { width: 250, quality: 85 });
              const CardContent = (
                <div>
                  <div className="h-28 flex items-center justify-center mb-6 bg-slate-50 p-2 rounded-lg border border-slate-100">
                    {partner.logo ? (
                      <figure className="m-0 flex flex-col items-center justify-center h-full w-full">
                        <img 
                          src={optimizedLogo} 
                          alt={partner.alt || `${partner.name} – Samarbejdspartner`} 
                          title={partner.name}
                          className="max-h-16 max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </figure>
                    ) : (
                      <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center rounded-lg">
                        <Handshake className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-400 block mb-2">
                    {partner.role}
                  </span>
                  <h3 className="text-xl font-bold uppercase tracking-tight mb-4 text-slate-900 group-hover:text-slate-700 transition-colors">
                    {partner.name}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {partner.description}
                  </p>
                </div>
              );

              return partner.website ? (
                <a
                  key={i}
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-slate-100 p-8 rounded-xl shadow-xs hover:shadow-md hover:border-slate-300 transition-all duration-300 flex flex-col justify-between group text-left"
                  title={`Besøg ${partner.name}`}
                >
                  {CardContent}
                </a>
              ) : (
                <div
                  key={i}
                  className="bg-white border border-slate-100 p-8 rounded-xl shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between group text-left"
                >
                  {CardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
}
